/**
 * 33. Budgeting — budget editor, versions, budget vs actual reports.
 *
 * Arabic and English (C7): every word from the catalogs; a budget's status
 * agrees with it.
 */
import { Card, PageHeader, DataTable, Skeleton, Badge } from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { statusLabel } from "@/lib/i18n/records";
import { formatMoney, formatQuantity } from "@/lib/money";

interface BudgetRow {
  id: string;
  name: string;
  fiscalYear: string;
  version: number;
  status: "Draft" | "Active" | "Superseded";
  totalBudget: string;
  totalActual: string;
  currencyCode: string;
}

export function BudgetingPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<BudgetRow>(
    ["finance", "budgeting"],
    "/finance/budgeting",
  );

  return (
    <div>
      <PageHeader
        title={t("finance.budgets.title")}
        subtitle={t("finance.budgets.subtitle")}
        actions={
          <PendingButton variant="primary" reason="afterLaunch" slice="F22">
            {t("finance.budgets.new")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("finance.budgets.list"), data.total)}>
          <DataTable<BudgetRow>
            rows={data.items}
            columns={[
              { key: "name", label: t("fields.name") },
              { key: "fiscalYear", label: t("finance.budgets.fiscalYear") },
              {
                key: "version",
                label: t("fields.version"),
                numeric: true,
                render: (r) => formatQuantity(r.version, 0),
              },
              {
                key: "totalBudget",
                label: t("finance.budgets.budget"),
                numeric: true,
                render: (r) => formatMoney(r.totalBudget, r.currencyCode),
              },
              {
                key: "totalActual",
                label: t("finance.budgets.actual"),
                numeric: true,
                render: (r) => formatMoney(r.totalActual, r.currencyCode),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <Badge
                    tone={
                      r.status === "Active"
                        ? "success"
                        : r.status === "Draft"
                          ? "warning"
                          : "default"
                    }
                  >
                    {statusLabel(t, "Budget", r.status)}
                  </Badge>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
